import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { teamsActions, teamMembersActions } from '../redux/actions';
import { nsOptions } from '../i18n';
import api from '../api';
import RowContent from './RowContent';
import LabeledInput from './LabeledInput';
import LabeledSelect from './LabeledSelect';
import { CardLoader } from './Loader';
import withLicenseMsgModal from './withLicenseMsgModal';
import ButtonConfirm from './ButtonConfirm';
import { DelMsgModal } from './MessageModal';
import { InvitationAvatar } from './avatar';
import EditButton from './EditButton';
import InvitationTooltipBase from './InvitationTooltipBase';
import Help from './Help';
import UserSelect from './UserSelect';
import SpecialtySelect from './SpecialtySelect';
import ErrorUtil from '../utils/ErrorUtil';
import { emailListToArray, deObfuscateEmail, formatPasteEmailList } from '../utils/email';
import Toast from '../utils/Toast';
import TimeoutHandler from '../utils/TimeoutHandler';
import { childrenPropTypes } from '../utils/generic-prop-types';
import TextUtil from '../utils/TextUtil';

import { DataLocker } from '../utils/data-util';
import { TEAM_LICENSE_PRO_KEY, TEAM_LICENSE_EXPERT_KEY } from '../utils/license';
import {
  LOCK_MODEL_TEAM_MEMBER, LOCK_MODEL_TEAM, LOCK_SECTION, LOCK_SECTION_NAME, LOCK_TOAST_TIMEOUT_S,
  TEAM_MEMBER_USUAL, NO_TEAM_LEADER, CAN_INVITE_TEAM_MEMBER, CAN_CREATE_PROJECT,
  CAN_MANAGE_TEAM_PERMISSIONS, CAN_VIEW_PROJECTS, CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
  CAN_VIEW_RESEARCH_TEAMS, CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
  CAN_MANAGE_PUBLIC_PROFILE, USER_TYPE_HEALTHCARE_FACILITY, TEAM_TYPE_ORGANIZATION,
} from '../constants';
import NewTooltip from './NewTooltip';
import fromReduxState from '../utils/redux';
import TeamMemberPermissionSettings from './TeamMemberPermissionSettings';

const ROLE_LEADER = 'leader';
const ROLE_MEMBER = 'member';

const InvitationStepLabel = (props) => (
  <div className="container">
    <div className="row align-items-center">
      <FontAwesomeIcon
        icon={['far', 'angle-double-right']}
        className="text-primary mr-2"
        transform="grow-1"
      />
      {props.label}
      {props.helpText && (
        <Help
          iconClassName="ml-2"
        >
          {props.helpText}
        </Help>
      )}
    </div>
  </div>
);

InvitationStepLabel.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

@withTranslation('', nsOptions)
class MemberRoleSelect extends Component {
    static propTypes = {
      className: PropTypes.string,
      value: PropTypes.string,
      defaultValue: PropTypes.string,
      t: PropTypes.func.isRequired,
      selectTextColor: PropTypes.string,
      readOnly: PropTypes.bool,
      label: PropTypes.string,
      onChange: PropTypes.func.isRequired,
      canInviteAsLeader: PropTypes.bool,
      hideLeaderOption: PropTypes.bool,
    };

    static defaultProps = {
      className: '',
      value: undefined,
      defaultValue: undefined,
      selectTextColor: undefined,
      readOnly: true,
      label: undefined,
      canInviteAsLeader: true,
      hideLeaderOption: false,
    };

    constructor(props) {
      super(props);
      this.state = {
        internalValue: props.defaultValue,
      };
    }

    onChange = async (e) => {
      const { canInviteAsLeader } = this.props;
      const { internalValue } = this.state;
      const prevInternalValue = internalValue;

      if (e.target.value === ROLE_LEADER && !canInviteAsLeader) {
        return false;
      }

      if (internalValue) {
        this.setState({ internalValue: e.target.value });
      }
      const success = await this.props.onChange(e);

      if (!success && prevInternalValue) {
        this.setState({ internalValue: prevInternalValue });
      }

      return success;
    };

    render() {
      const {
        t, className, value, selectTextColor, readOnly, label, canInviteAsLeader,
        hideLeaderOption,
      } = this.props;
      const { internalValue } = this.state;
      const effectiveValue = internalValue || value;

      return (
        <LabeledSelect
          name="role"
          className={className}
          label={label}
          selectClassName={`custom-select team-role-select ${selectTextColor ? ` ${selectTextColor}` : ''}`}
          colSelectClassName="col-12 col-sm-12 col-md-10 col-lg-8 col-xl-5"
          value={effectiveValue}
          disabled={readOnly}
          hideOptionalLabel
          onChange={this.onChange}
        >
          <option
            key={ROLE_MEMBER}
            value={ROLE_MEMBER}
          >
            { t('user:team.role.member') }
          </option>
          {!hideLeaderOption && (
          <option
            key={ROLE_LEADER}
            value={ROLE_LEADER}
            disabled={!canInviteAsLeader}
            className={!canInviteAsLeader ? 'text-muted' : ''}
          >
            {!canInviteAsLeader
              ? `${t('user:team.role.leader')} (${t('user:team.role.owner-restricted')})`
              : t('user:team.role.leader')}
          </option>
          )}
        </LabeledSelect>
      );
    }
}


const memberTooltipMapStateToProps = () => ({
});

const memberTooltimapDispatchToProps = (dispatch, ownProps) => ({
  addTeamMember: async (data) => dispatch(teamMembersActions.create(data,
    { admin: ownProps.admin })),
  patchTeamMember: async (id, data) => dispatch(teamMembersActions.patch(id, data,
    { admin: ownProps.admin })),
  deleteTeamMember: async (id) => dispatch(teamMembersActions.remove(id,
    { admin: ownProps.admin })),
  resyncTeam: async (id) => dispatch(teamsActions.read(id)),
});

@withToastManager
@connect(memberTooltipMapStateToProps, memberTooltimapDispatchToProps, null, { forwardRef: true })
@withTranslation('', nsOptions)
class MemberTooltip extends Component {
    static propTypes = {
      children: childrenPropTypes().isRequired,
      member: PropTypes.shape().isRequired,
      t: PropTypes.func.isRequired,
      readOnly: PropTypes.bool,
      patchTeamMember: PropTypes.func.isRequired,
      addTeamMember: PropTypes.func.isRequired,
      deleteTeamMember: PropTypes.func.isRequired,
      resyncTeam: PropTypes.func.isRequired,
      admin: PropTypes.bool,
      teamFrozen: PropTypes.bool,
      teamMembers: PropTypes.shape(),
      userId: PropTypes.number,
      // eslint-disable-next-line react/no-unused-prop-types
      user: PropTypes.shape(),
      team: PropTypes.shape(),
      // blockGuest: PropTypes.func,
    };

    static defaultProps = {
      readOnly: true,
      admin: false,
      teamFrozen: false,
      teamMembers: {},
      userId: null,
      user: null,
      team: null,
      // blockGuest: () => {},
    };

    constructor(props) {
      super(props);

      this.state = {
        editing: false,
      };
    }

    onRoleChange = async (e) => {
      try {
        const { patchTeamMember, member, resyncTeam } = this.props;
        const { value } = e.target;
        const data = {};
        switch (value) {
          case ROLE_LEADER:
            data.leader = true;
            break;
          case ROLE_MEMBER:
            data.leader = false;
            break;
          default:
            console.error('Not expected value');
            return false;
        }
        await patchTeamMember(member.id, data);
        if (data.leader) {
          // Resync the team in case the team had no leader and was frozen
          await resyncTeam(member.team);
        }
        Toast.success(this.props, 'error:valid.success');
        return true;
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
        return false;
      }
    };

    handleEditionToggle = (editing) => {
      this.setState({ editing });
    };

    onPermissionToggle = async (e) => {
      try {
        const { patchTeamMember, member } = this.props;
        const permission = e.target.value;
        const isChecked = e.target.checked;

        const currentPermissions = member.team_permissions
          ? member.team_permissions.map((p) => p.name)
          : [];

        let newPermissions;
        if (isChecked) {
          newPermissions = [...currentPermissions, permission];
        } else {
          newPermissions = currentPermissions.filter((p) => p !== permission);
        }

        await patchTeamMember(member.id, { permissions: newPermissions });
        Toast.success(this.props, 'error:valid.success');
        return true;
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
        return false;
      }
    };

    resendInvitation = async (invitation) => {
      try {
        const { addTeamMember } = this.props;
        const { team, email, leader } = invitation;
        await addTeamMember({ team, email, leader });
        Toast.success(this.props, 'error:valid.success');
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      }
    };

    remove = async (invitation) => {
      try {
        const { deleteTeamMember } = this.props;
        await deleteTeamMember(invitation.id);
        Toast.success(this.props, 'error:valid.success');
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      }
    };

    copyInvitationLink = async (invitation) => {
      const { admin } = this.props;
      TextUtil.asyncCopyTextToClipboard(
        this.props,
        api.requestData(
          `team-members/${invitation.id}/invitation-url`,
          null, 'get', { admin },
        ),
        (response) => response.url,
      );
    }

    render() {
      const {
        t, member, children, readOnly, admin, teamFrozen, teamMembers, userId,
        team,
      } = this.props;
      const { editing } = this.state;
      const { confirmed, email } = member;
      const teamMembersArray = teamMembers ? Object.values(teamMembers) : [];
      const currentUserMember = teamMembersArray.find(
        (mb) => mb.user?.id === userId && mb.team === member.team,
      );

      const currentUserIsLeader = currentUserMember?.leader || false;
      // eslint-disable-next-line max-len, camelcase
      const currentUserCanManagePermissions = currentUserMember?.team_permissions?.some((p) => p.name === CAN_MANAGE_TEAM_PERMISSIONS) || false;
      // eslint-disable-next-line max-len, camelcase
      const currentUserCanInviteMembers = currentUserMember?.team_permissions?.some((p) => p.name === CAN_INVITE_TEAM_MEMBER) || false;

      const currentUserIsOwner = team && team.owner && team.owner.id === userId;
      // eslint-disable-next-line camelcase
      const canAdministrateViaAdminTeam = team?.current_user_can_administrate || false;
      const canManageLeaderPermissions = admin || currentUserIsOwner || canAdministrateViaAdminTeam;
      // eslint-disable-next-line max-len
      const canManageMemberPermissions = admin || currentUserIsOwner || currentUserCanManagePermissions || canAdministrateViaAdminTeam;

      const targetIsLeader = member.leader;
      const canSeeAndManagePermissions = targetIsLeader
        ? canManageLeaderPermissions
        : canManageMemberPermissions;

      const isEditingSelf = member.user?.id === userId;

      const permissionsReadOnly = !canSeeAndManagePermissions || teamFrozen;
      const isAdminTeam = team?.type === TEAM_TYPE_ORGANIZATION;
      const isInstitution = team?.owner?.type === USER_TYPE_HEALTHCARE_FACILITY;

      let writablePermissions = [];
      if (canSeeAndManagePermissions && !teamFrozen) {
        if (isAdminTeam) {
          // Admin team permissions
          writablePermissions = [
            CAN_VIEW_PROJECTS,
            CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
            CAN_MANAGE_PUBLIC_PROFILE,
            CAN_INVITE_TEAM_MEMBER,
            CAN_MANAGE_TEAM_PERMISSIONS,
          ];
          if (isInstitution) {
            writablePermissions.push(
              CAN_VIEW_RESEARCH_TEAMS,
              CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
            );
          }
        } else {
          // Standard team permissions
          writablePermissions = [
            CAN_CREATE_PROJECT, CAN_INVITE_TEAM_MEMBER, CAN_MANAGE_TEAM_PERMISSIONS,
          ];
        }
      }

      const canManagePermissions = canSeeAndManagePermissions && !permissionsReadOnly
            && team.owner.id !== member.user?.id
            && !isEditingSelf;

      // Admin team members allowed to invite (or manage permissions) can also remove members
      const canRemoveAdminTeamMember = isAdminTeam && member.type === TEAM_MEMBER_USUAL
        && !isEditingSelf
        && (currentUserCanInviteMembers || currentUserCanManagePermissions);
      // eslint-disable-next-line max-len
      const canRemoveMember = admin || currentUserIsOwner || canAdministrateViaAdminTeam || (currentUserIsLeader && !targetIsLeader) || canRemoveAdminTeamMember;

      return (
        <InvitationTooltipBase
          admin={admin}
          dataLockerType={LOCK_MODEL_TEAM_MEMBER}
          invitation={member}
          showManagement={canManagePermissions || canRemoveAdminTeamMember}
          canRemove={canRemoveMember}
          resendInvitation={this.resendInvitation}
          cancelInvitation={this.remove}
          removeGuest={this.remove}
          copyInvitationLink={this.copyInvitationLink}
          // blockGuest={false}
          onEditionToggle={this.handleEditionToggle}
          removeGuestLabel="user:team.remove-from-team"
          removeGuestWarning={(
            <span>
              {t('user:team.remove-from-team-warning')}
              <br />
              <br />
              {t('common:confirm-deletion')}
            </span>
          )}
          extraContent={(
            <>
              <div className="font-italic mb-2">
                {confirmed ? email : t('user:team.invitations.pending')}
              </div>
              {!isAdminTeam && (
                <MemberRoleSelect
                  defaultValue={member.leader ? ROLE_LEADER : ROLE_MEMBER}
                  selectTextColor="text-white"
                  readOnly={readOnly || teamFrozen || !canSeeAndManagePermissions}
                  onChange={this.onRoleChange}
                  canInviteAsLeader={canManageLeaderPermissions}
                />
              )}

              <TeamMemberPermissionSettings
                className="team-member-permissions-tooltip mt-3"
                value={member.team_permissions
                  ? member.team_permissions.map((p) => p.name) : []}
                defaultValue={member.team_permissions
                  ? member.team_permissions.map((p) => p.name) : []}
                readOnly={!editing}
                writablePermissions={writablePermissions}
                isLeader={targetIsLeader}
                onChange={this.onPermissionToggle}
                ref={(ref) => { this.permissionsRef = ref; }}
                showOwnerOnlyMessage={targetIsLeader && !canManageLeaderPermissions}
                showCannotEditOwnPermissions={isEditingSelf && canSeeAndManagePermissions}
                isAdminTeam={isAdminTeam}
                isInstitution={isInstitution}
              />
            </>
                )}
        >
          {children}
        </InvitationTooltipBase>
      );
    }
}

// eslint-disable-next-line react/prefer-stateless-function
@withTranslation('', nsOptions)
class MemberAvatar extends Component {
    static propTypes = {
      member: PropTypes.shape().isRequired,
      readOnly: PropTypes.bool,
      admin: PropTypes.bool,
      teamFrozen: PropTypes.bool,
      teamMembers: PropTypes.shape(),
      userId: PropTypes.number,
      user: PropTypes.shape(),
      team: PropTypes.shape(),
      blockGuest: PropTypes.func,
      fetchTeamMembers: PropTypes.func,
    };

    static defaultProps = {
      readOnly: true,
      admin: false,
      teamFrozen: false,
      teamMembers: {},
      userId: null,
      user: null,
      team: null,
      blockGuest: () => {},
      fetchTeamMembers: () => {},
    };

    render() {
      const {
        member, readOnly, admin, teamFrozen, teamMembers, userId, user,
        blockGuest, fetchTeamMembers, team,
      } = this.props;

      return (
        <div className="mr-2 mt-2 position-relative d-inline" key={member.id}>
          <MemberTooltip
            member={member}
            readOnly={readOnly}
            admin={admin}
            teamFrozen={teamFrozen && !member.leader}
            teamMembers={teamMembers}
            userId={userId}
            user={user}
            team={team}
            blockGuest={blockGuest}
            fetchTeamMembers={fetchTeamMembers}
          >
            <InvitationAvatar
              user={member.user}
              email={member.email}
              confirmed={member.confirmed}
            />
          </MemberTooltip>
        </div>
      );
    }
}

@withTranslation('', nsOptions)
class MemberList extends Component {
    static propTypes = {
      t: PropTypes.func.isRequired,
      role: PropTypes.oneOf([ROLE_LEADER, ROLE_MEMBER]).isRequired,
      className: PropTypes.string,
      children: childrenPropTypes().isRequired,
      teamMembers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
      readOnly: PropTypes.bool,
      userId: PropTypes.number.isRequired,
      admin: PropTypes.bool,
      canModifyItsOwnRole: PropTypes.bool,
      teamFrozen: PropTypes.bool,
      allTeamMembers: PropTypes.shape(),
      user: PropTypes.shape(),
      team: PropTypes.shape(),
      blockGuest: PropTypes.func,
      fetchTeamMembers: PropTypes.func,
    };

    static defaultProps = {
      className: '',
      readOnly: true,
      admin: false,
      canModifyItsOwnRole: false,
      teamFrozen: false,
      allTeamMembers: {},
      user: null,
      team: null,
      blockGuest: () => {},
      fetchTeamMembers: () => {},
    };

    render() {
      const {
        t, role, className, children, teamMembers, readOnly, userId, admin, canModifyItsOwnRole,
        teamFrozen, allTeamMembers, user, blockGuest, fetchTeamMembers, team,
      } = this.props;

      const teamFrozenAndNoMember = teamFrozen && role === ROLE_MEMBER;

      return (
        <div className={className}>
          <div className="row">
            <div className="col">
              <Label className="mb-2">
                {children}
              </Label>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-sm">
              {
                            teamMembers.length > 0 ? teamMembers.map((member) => (
                              <MemberAvatar
                                key={member.id}
                                member={member}
                                readOnly={readOnly || (member.user.id === userId
                                        && !canModifyItsOwnRole)
                                      || team.owner.id === member.user.id}
                                admin={admin}
                                teamFrozen={teamFrozen}
                                teamMembers={allTeamMembers}
                                userId={userId}
                                user={user}
                                team={team}
                                blockGuest={blockGuest}
                                fetchTeamMembers={fetchTeamMembers}
                              />
                            )) : (
                              <div className={`font-italic ${teamFrozenAndNoMember ? 'text-red font-weight-semibold' : 'text-gray'} pt-1`}>
                                {teamFrozenAndNoMember ? t('user:team.cannot-invite-members')
                                  : t('user:team.invitations.none')}
                              </div>
                            )
                        }
            </div>
          </div>
        </div>
      );
    }
}

const mapStateToProps = (state) => ({
  userId: state.auth.authUser.id,
  user: state.auth.authUser,
  teamLicenses: state.teamLicenses,
  teamMembers: state.teamMembers,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchTeams: async (userId) => dispatch(teamsActions.list(
    ownProps.admin ? { admin: true } : { members__user: userId },
    { pagination: 'no' },
  )),
  patchTeam: async (id, data) => dispatch(teamsActions.patch(id, data, { admin: ownProps.admin })),
  deleteTeam: async (id) => dispatch(teamsActions.remove(id, { admin: ownProps.admin })),
  fetchTeamMembers: async (teamId) => dispatch(teamMembersActions.list({
    team: teamId,
  }, { pagination: 'no' })),
  addTeamMember: async (data) => dispatch(teamMembersActions.create(data,
    { admin: ownProps.admin })),
  deleteTeamMember: async (id) => dispatch(teamMembersActions.remove(id,
    { admin: ownProps.admin })),
  patchTeamMember: async (id, data) => dispatch(teamMembersActions.patch(id, data,
    { admin: ownProps.admin })),
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })
@withTranslation('', nsOptions)
@withRouter
@withLicenseMsgModal()
class TeamForm extends Component {
    static propTypes = {
      t: PropTypes.func.isRequired,
      admin: PropTypes.bool,
      team: PropTypes.shape(),
      teamLicenses: PropTypes.shape(),
      teamMembers: PropTypes.shape(),
      userId: PropTypes.number.isRequired,
      user: PropTypes.shape().isRequired,
      fetchTeams: PropTypes.func.isRequired,
      patchTeam: PropTypes.func.isRequired,
      deleteTeam: PropTypes.func.isRequired,
      fetchTeamMembers: PropTypes.func.isRequired,
      addTeamMember: PropTypes.func.isRequired,
      deleteTeamMember: PropTypes.func.isRequired,
      patchTeamMember: PropTypes.func.isRequired,
      userOwnTeams: PropTypes.bool,
      focusTeamNameOnOpen: PropTypes.bool,
      onOpen: PropTypes.func,
      onQuitTeam: PropTypes.func,
      onDeleteTeam: PropTypes.func,
    };

    static defaultProps = {
      admin: false,
      team: undefined,
      teamLicenses: {},
      teamMembers: [],
      userOwnTeams: false,
      focusTeamNameOnOpen: false,
      onOpen: () => {},
      onQuitTeam: () => {},
      onDeleteTeam: () => {},
    };

    static getExtraInvestigators = (team) => {
      const value = team.licence_options.max_investigators_per_project;
      return !value ? '0' : value.toString();
    };

    static isTeamOwner = (team, userId) => team.owner.id === userId;

    static isTeamLeader = (userId, teamId, teamMembers) => {
      if (!teamMembers || !teamMembers.length) return false;
      return teamMembers.findIndex((member) => member.user.id === userId && member.leader
            && member.team === teamId) >= 0;
    };

    static isOwnerOrLeader = (userId, team, teamMembers) => {
      if (!userId || !team) return false;
      if (TeamForm.isTeamOwner(team, userId)) return true;
      return TeamForm.isTeamLeader(userId, team.id, teamMembers);
    };

    static getInitialState = (props) => {
      const getCurrentUserPermissions = () => {
        if (!props.team || !props.teamMembers || !props.userId) {
          return {
            canCreateProject: false,
            canInviteMembers: false,
            canManagePermissions: false,
            isLeader: false,
          };
        }

        const teamMembersArray = Object.values(props.teamMembers);
        const currentUserMember = teamMembersArray.find(
          (mb) => mb.user?.id === props.userId && mb.team === props.team.id,
        );

        if (!currentUserMember) {
          return {
            canCreateProject: false,
            canInviteMembers: false,
            canManagePermissions: false,
            isLeader: false,
          };
        }

        const permissions = currentUserMember.team_permissions || [];
        const isLeader = currentUserMember.leader;

        return {
          canCreateProject: permissions.some((p) => p.name === CAN_CREATE_PROJECT),
          canInviteMembers: permissions.some((p) => p.name === CAN_INVITE_TEAM_MEMBER),
          canManagePermissions: permissions.some((p) => p.name === CAN_MANAGE_TEAM_PERMISSIONS),
          isLeader,
        };
      };

      const userPermissions = getCurrentUserPermissions();

      // Check if owner is read-only for organization-owned research teams
      const isOwner = props.team && TeamForm.isTeamOwner(props.team, props.userId);
      // eslint-disable-next-line camelcase
      const isOwnerReadOnly = props.team?.is_owner_read_only_for_research_teams || false;
      // eslint-disable-next-line camelcase
      const canAdministrateViaAdminTeam = props.team?.current_user_can_administrate || false;

      // Owner can edit/delete team name ONLY if not read-only
      // Admin team members with permission can also edit/delete
      const canEditAndDeleteTeam = props.team
        ? (isOwner && !isOwnerReadOnly) || canAdministrateViaAdminTeam || props.admin
        : false;

      // Owner can manage members ONLY if not read-only
      // Leaders can still manage members
      // Admin team members with permission can also manage
      const isLeaderNotOwner = props.team
        ? TeamForm.isTeamLeader(props.userId, props.team.id, Object.values(props.teamMembers))
        : false;
      const canManageMembers = props.team
        ? (isOwner && !isOwnerReadOnly)
          || isLeaderNotOwner
          || canAdministrateViaAdminTeam
          || props.admin
        : false;

      return {
        id: props.team ? props.team.id : undefined,
        name: props.team ? props.team.name : '',
        type: props.team ? props.team.licence : undefined,
        owner: props.team ? props.team.owner.id : undefined,
        ownerLabel: props.team ? props.team.owner.label : undefined,
        extraInvestigators: props.team ? TeamForm.getExtraInvestigators(props.team) : undefined,
        isFrozen: props.team ? props.team.is_frozen : undefined,
        canManageMembers,
        canEditAndDeleteTeam,
        specialties: props.team ? props.team.specialties : [],
        currentUserCanCreateProject: userPermissions.canCreateProject,
        currentUserCanInviteMembers: userPermissions.canInviteMembers,
        currentUserCanManagePermissions: userPermissions.canManagePermissions,
        currentUserIsLeader: userPermissions.isLeader,
      };
    };

    static getInvitationRole = (props) => {
      const { team } = props;
      if (!team) return ROLE_MEMBER;
      // Cannot invite members if there is no leader
      if (team.is_frozen && team.frozen_reason === NO_TEAM_LEADER) return ROLE_LEADER;
      return ROLE_MEMBER;
    };

    constructor(props) {
      super(props);
      const initialState = TeamForm.getInitialState(props);

      this.state = {
        loading: false,
        nameEditing: false,
        invitationCreationInProgress: false,
        processingInvitation: false,
        invitationEmail: '',
        invitationEmailValid: false,
        invitationRole: TeamForm.getInvitationRole(props),
        invitationCanCreateProject: false,
        invitationCanInviteMembers: false,
        invitationCanManagePermissions: false,
        // Organization team permissions
        invitationCanViewProjects: false,
        invitationCanCreateAndAdministrateProjects: false,
        invitationCanManagePublicProfile: false,
        invitationCanViewResearchTeams: false,
        invitationCanCreateAndAdministrateResearchTeams: false,
        ...initialState,
      };
      this.timeoutHandler = new TimeoutHandler();
      this.nameLocker = new DataLocker(LOCK_MODEL_TEAM, LOCK_SECTION, props.admin);
      this.specialtySelect = null;
      this.toTeamMembersArray = fromReduxState((teamMembers) => Object.values(teamMembers));
    }

    async componentDidMount() {
      const { focusTeamNameOnOpen, onOpen } = this.props;
      const { nameEditing } = this.state;
      if (focusTeamNameOnOpen && !nameEditing) {
        await this.onNameEditionToggle();
        if (this.nameInput) {
          const input = document.getElementById(this.nameInput.id);
          if (input) input.focus();
        }
      }
      onOpen();
    }

    componentDidUpdate(prevProps) {
      const { team: prevTeam, teamMembers: prevTeamMembers } = prevProps;
      const { team, teamMembers } = this.props;

      if (prevTeam && team && team.is_frozen !== prevTeam.is_frozen) {
        this.updateFrozenState();
      }
      if (!prevTeam && team && team.is_frozen) {
        this.updateInvitationRole();
      }

      if (teamMembers !== prevTeamMembers) {
        this.updateCurrentUserPermissions();
      }

      // Update management permissions when team permission fields change
      // eslint-disable-next-line camelcase
      const prevReadOnly = prevTeam?.is_owner_read_only_for_research_teams;
      // eslint-disable-next-line camelcase
      const currReadOnly = team?.is_owner_read_only_for_research_teams;
      // eslint-disable-next-line camelcase
      const prevCanAdmin = prevTeam?.current_user_can_administrate;
      // eslint-disable-next-line camelcase
      const currCanAdmin = team?.current_user_can_administrate;

      if (prevReadOnly !== currReadOnly || prevCanAdmin !== currCanAdmin
          || (!prevTeam && team)) {
        this.updateManagementPermissions();
      }
    }

    componentWillUnmount() {
      this.closeAllEditions();
    }

    resetTeam = () => this.setState({ ...TeamForm.getInitialState(this.props) });

    updateFrozenState = () => {
      const { team } = this.props;
      this.setState({ isFrozen: team ? team.is_frozen : undefined });
    };

    updateInvitationRole = () => {
      const newInvitationRole = TeamForm.getInvitationRole(this.props);
      this.setState({ invitationRole: newInvitationRole });
    };

    updateManagementPermissions = () => {
      const {
        team, teamMembers, userId, admin,
      } = this.props;

      if (!team) return;

      const isOwner = TeamForm.isTeamOwner(team, userId);
      // eslint-disable-next-line camelcase
      const isOwnerReadOnly = team?.is_owner_read_only_for_research_teams || false;
      // eslint-disable-next-line camelcase
      const canAdministrateViaAdminTeam = team?.current_user_can_administrate || false;

      const canEditAndDeleteTeam = (isOwner && !isOwnerReadOnly)
        || canAdministrateViaAdminTeam || admin;

      const isLeaderNotOwner = TeamForm.isTeamLeader(
        userId,
        team.id,
        Object.values(teamMembers),
      );
      const canManageMembers = (isOwner && !isOwnerReadOnly)
        || isLeaderNotOwner
        || canAdministrateViaAdminTeam
        || admin;

      this.setState({ canManageMembers, canEditAndDeleteTeam });
    };

    updateCurrentUserPermissions = () => {
      const { team, teamMembers, userId } = this.props;

      if (!team || !teamMembers || !userId) return;

      const teamMembersArray = Object.values(teamMembers);
      const currentUserMember = teamMembersArray.find(
        (mb) => mb.user?.id === userId && mb.team === team.id,
      );

      if (!currentUserMember) {
        this.setState({
          // eslint-disable-next-line react/no-unused-state
          currentUserCanCreateProject: false,
          currentUserCanInviteMembers: false,
          // eslint-disable-next-line react/no-unused-state
          currentUserCanManagePermissions: false,
          // eslint-disable-next-line react/no-unused-state
          currentUserIsLeader: false,
        });
        return;
      }

      const permissions = currentUserMember.team_permissions || [];
      const isLeader = currentUserMember.leader;

      this.setState({
        // eslint-disable-next-line react/no-unused-state
        currentUserCanCreateProject: permissions.some((p) => p.name === CAN_CREATE_PROJECT),
        currentUserCanInviteMembers: permissions.some((p) => p.name === CAN_INVITE_TEAM_MEMBER),
        // eslint-disable-next-line react/no-unused-state,max-len
        currentUserCanManagePermissions: permissions.some((p) => p.name === CAN_MANAGE_TEAM_PERMISSIONS),
        // eslint-disable-next-line react/no-unused-state
        currentUserIsLeader: isLeader,
      });
    };

    userHasPermission = (permissionName) => {
      const {
        team, teamMembers, userId, admin,
      } = this.props;

      if (admin) return true;

      if (!team || !teamMembers || !userId) return false;

      if (team.owner.id === userId) return true;

      // eslint-disable-next-line camelcase
      const canAdministrateViaAdminTeam = team?.current_user_can_administrate || false;
      if (canAdministrateViaAdminTeam) return true;

      const teamMembersArray = Object.values(teamMembers);
      const currentUserMember = teamMembersArray.find(
        (mb) => mb.user?.id === userId && mb.team === team.id,
      );

      if (!currentUserMember) return false;

      const permissions = currentUserMember.team_permissions || [];

      // For admin teams, if user has CAN_MANAGE_TEAM_PERMISSIONS, they can grant all permissions
      const isAdminTeam = team?.type === TEAM_TYPE_ORGANIZATION;
      if (isAdminTeam && permissions.some((p) => p.name === CAN_MANAGE_TEAM_PERMISSIONS)) {
        return true;
      }

      return permissions.some((p) => p.name === permissionName);
    };

    isCurrentUserMember = () => {
      const { team, teamMembers, userId } = this.props;

      if (!team || !teamMembers || !userId) return false;

      const teamMembersArray = Object.values(teamMembers);
      const currentUserMember = teamMembersArray.find(
        (mb) => mb.user?.id === userId && mb.team === team.id,
      );

      return Boolean(currentUserMember);
    };

    blockGuest = async (teamMember) => {
      // eslint-disable-next-line react/prop-types
      const { patchTeamMember, fetchTeamMembers } = this.props;
      const { id } = this.state;

      try {
        await patchTeamMember(teamMember.id, {
          permissions: [],
        });

        await fetchTeamMembers(id);

        Toast.success(this.props, 'error:valid.success');
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      }
    };

    onNameEditionToggle = async () => {
      const { t } = this.props;
      const { id } = this.state;

      try {
        const res = await this.nameLocker.toggleLock(id, LOCK_SECTION_NAME);
        const { locked, success } = res;
        if (success) {
          this.setState({ nameEditing: locked });
        } else {
          Toast.warning(this.props, DataLocker.lockUserMessage(res, t), LOCK_TOAST_TIMEOUT_S);
        }
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      }
    }

    onEmailInvitationChange = (e) => {
      const value = e.target ? e.target.value : null;
      this.setState({
        invitationEmail: value,
        invitationEmailValid: this.emailValidator.validate(value),
      });
    }

    onEmailInvitationPaste = (e) => {
      formatPasteEmailList(e);
      this.onEmailInvitationChange(e);
    }

    isInvitationFormValid = () => this.state.invitationEmailValid;

    closeAllEditions = (updateState = false) => {
      if (this.nameLocker.isLocked()) this.nameLocker.unlock();
      if (updateState) {
        this.setState({
          nameEditing: false,
        });
      }
    }

    updateTeam = async (field, value, undoValueIfError = true, throwError = false) => {
      const { team } = this.props;
      const { id } = this.state;
      try {
        const { patchTeam } = this.props;
        await patchTeam(id, { [field]: value });
        this.setState({ [field]: value });
        Toast.success(this.props, 'error:valid.success');
      } catch (error) {
        if (undoValueIfError && team) {
          this.setState({ [field]: team[field] });
        }
        if (throwError) {
          throw error;
        } else {
          ErrorUtil.handleCatched(this.props, error);
        }
      }
    }

    deleteTeam = async () => {
      this.setState({ loading: true });
      try {
        const { deleteTeam, onDeleteTeam } = this.props;
        const { id } = this.state;
        await deleteTeam(id);
        Toast.success(this.props, 'error:valid.success');
        await onDeleteTeam();
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      } finally {
        this.setState({ loading: false });
      }
    }

    quitTeam = async () => {
      this.setState({ loading: true });
      try {
        const {
          teamMembers: tmMembers, deleteTeamMember, userId, fetchTeams, onQuitTeam,
        } = this.props;
        const { id } = this.state;
        const teamMembers = this.toTeamMembersArray(tmMembers);
        const teamMember = teamMembers.find((tmMember) => (
          tmMember.team === id && tmMember.user.id === userId
        ));
        await deleteTeamMember(teamMember.id);
        await fetchTeams(userId);
        Toast.success(this.props, 'error:valid.success');
        await onQuitTeam();
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      } finally {
        this.setState({ loading: false });
      }
    }

    invite = async () => {
      this.setState({ processingInvitation: true });
      try {
        const { addTeamMember, fetchTeamMembers, admin } = this.props;
        const {
          id,
          invitationEmail,
          invitationRole,
          invitationCanCreateProject,
          invitationCanInviteMembers,
          invitationCanManagePermissions,
          // Organization team permissions
          invitationCanViewProjects,
          invitationCanCreateAndAdministrateProjects,
          invitationCanManagePublicProfile,
          invitationCanViewResearchTeams,
          invitationCanCreateAndAdministrateResearchTeams,
        } = this.state;
        const { team } = this.props;
        const emails = emailListToArray(invitationEmail);
        // For admin teams, members can never be leaders
        const isAdminTeam = team?.type === TEAM_TYPE_ORGANIZATION;
        const leader = isAdminTeam ? false : invitationRole === ROLE_LEADER;

        const permissions = [];
        // Standard team permissions
        if (invitationCanCreateProject) {
          permissions.push('can_create_project');
        }
        if (invitationCanInviteMembers) {
          permissions.push('can_invite_team_member');
        }
        if (invitationCanManagePermissions) {
          permissions.push('can_manage_team_permissions');
        }
        // Organization team permissions
        if (invitationCanViewProjects) {
          permissions.push('can_view_projects');
        }
        if (invitationCanCreateAndAdministrateProjects) {
          permissions.push('can_create_and_administrate_projects');
        }
        if (invitationCanManagePublicProfile) {
          permissions.push('can_manage_public_profile');
        }
        if (invitationCanViewResearchTeams) {
          permissions.push('can_view_research_teams');
        }
        if (invitationCanCreateAndAdministrateResearchTeams) {
          permissions.push('can_create_and_administrate_research_teams');
        }

        if (emails.length === 1) {
          await addTeamMember({
            team: id,
            email: emails[0],
            leader,
            permissions,
          });
        } else {
          await api.create('bulk-invite-in-team', {
            team: id,
            emails,
            leader,
            permissions,
          }, { admin });
          await fetchTeamMembers(id);
        }
        Toast.success(this.props, 'error:valid.saved');
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      } finally {
        this.setState({
          invitationEmail: '',
          invitationEmailValid: false,
          invitationRole: TeamForm.getInvitationRole(this.props),
          invitationCanCreateProject: false,
          invitationCanInviteMembers: false,
          invitationCanManagePermissions: false,
          // Reset organization team permissions
          invitationCanViewProjects: false,
          invitationCanCreateAndAdministrateProjects: false,
          invitationCanManagePublicProfile: false,
          invitationCanViewResearchTeams: false,
          invitationCanCreateAndAdministrateResearchTeams: false,
          processingInvitation: false,
          invitationCreationInProgress: false,
        });
      }
    };

    renderName = () => {
      const { t } = this.props;
      const {
        name, nameEditing, canEditAndDeleteTeam, isFrozen,
      } = this.state;
      const canEditName = canEditAndDeleteTeam && nameEditing;
      return (
        <>
          <LabeledInput
            className={!canEditName ? 'read-only' : ''}
            name="name"
            type="text"
            placeholder={t('user:team.placeholder.name')}
            colInputClassName="col-12"
            value={name}
            readOnly={!canEditName}
            required
            onChange={(e) => {
              const { value } = e.target;
              const undoValueIfError = value !== '';
              this.setState({ name: value });
              this.timeoutHandler.doAfterTimeout(() => this.updateTeam('name', value, undoValueIfError));
            }}
            inputClassName="team-name-input labeled-input"
            ref={(ref) => { this.nameInput = ref; }}
          />
          {isFrozen && (
            <div className="col-12 px-0">
              <NewTooltip
                content={t('user:team.frozen.help')}
              >
                <span>
                  <FontAwesomeIcon
                    icon={['fas', 'exclamation-triangle']}
                    transform="grow-2"
                    className="text-red"
                  />
                  <span className="ml-3 dotted-underline font-weight-semibold text-red">
                    {t('user:team.frozen.info')}
                  </span>
                </span>
              </NewTooltip>
            </div>
          )}
        </>
      );
    };

    renderOwner = () => {
      const { fetchTeamMembers, admin } = this.props;
      const { owner, id, ownerLabel } = this.state;

      return admin ? (
        <div className="row">
          <div className="col-12 mb-3">
            <UserSelect
              admin
              value={owner}
              onChange={async (e) => {
                await this.updateTeam('owner', e.value, false);
                await fetchTeamMembers(id);
                this.resetTeam();
              }}
            />
          </div>
        </div>
      ) : (
        ownerLabel && (
        <div
          className="mt-2"
          style={{ paddingTop: '0.15rem' }}
        >
          {ownerLabel}
        </div>
        )
      );
    }

    renderAdministrators = () => {
      const { team } = this.props;
      const { administrators } = team || {};

      if (!administrators || administrators.length === 0) {
        return null;
      }

      return (
        <div
          className="mt-2"
          style={{ paddingTop: '0.15rem' }}
        >
          {administrators.map((admin) => (
            <div key={admin.id} className="mb-1">
              {admin.label}
            </div>
          ))}
        </div>
      );
    }

    getLicenseSpecs = (license) => {
      if (license.key === TEAM_LICENSE_PRO_KEY) {
        return [
          { key: 'spec-1', access: true, value: 'participate-in-projects' },
          { key: 'spec-2', access: false, value: 'create-projects' },
        ];
      }
      return [
        { key: 'spec-1', access: true, value: 'participate-in-projects' },
        { key: 'spec-2', access: true, value: 'create-projects' },
      ];
    }

    renderType = () => {
      const { t, teamLicenses, admin } = this.props;
      const { type, extraInvestigators } = this.state;
      const upgradeLicenseEmail = deObfuscateEmail('support', 'doqboard.com');
      const teamLicense = teamLicenses[type];
      const teamLicenseSpecs = teamLicense ? this.getLicenseSpecs(teamLicense) : [];

      return (
        teamLicense && (
        <>
          <div className="font-weight-semibold">
            {t(`user:licenses.team-${teamLicense.key}`)}
          </div>
          {admin && extraInvestigators !== undefined && (
          <div>
            <span className="mt-1 font-italic">Max number of external investigators:</span>
            <span>{` ${extraInvestigators}`}</span>
          </div>
          )}
          {!admin && (
          <>
            <ul className="list-group py-2">
              {
                                    teamLicenseSpecs.map((spec) => {
                                      const { key, access, value } = spec;
                                      return (
                                        <li
                                          key={key}
                                          className={`${access ? '' : 'text-gray-dark'} list-group-item border-0 pl-0 py-1 bg-transparent`}
                                        >
                                          <span className="pr-1">
                                            <FontAwesomeIcon
                                              className={`fa-fw ${access ? 'text-green' : 'text-red'}`}
                                              icon={access ? ['fas', 'check'] : ['fas', 'times']}
                                            />
                                          </span>
                                          <span>
                                            {t(`user:team.specs.${value}`)}
                                          </span>
                                        </li>
                                      );
                                    })
                                }
            </ul>
            {teamLicense.key !== TEAM_LICENSE_EXPERT_KEY && (
            <span>
              {t('user:license-team-info')}
                                    &nbsp;
              <span className="font-weight-semibold">
                {upgradeLicenseEmail}
              </span>
              .
            </span>
            )}
          </>
          )}
        </>
        )
      );
    };

    renderSpecialties = () => {
      const { t, admin } = this.props;
      const { specialties } = this.state;

      if (admin) {
        return (
          <SpecialtySelect
            admin={admin}
            values={specialties}
            onChange={(newSpecialties) => this.updateTeam(
              'specialties', newSpecialties.map((specialty) => specialty.value), true, true,
            )}
            disabled={!admin}
            isMulti
            ref={(ref) => { this.specialtySelect = ref; }}
            placeholder={t('user:team.select-team-specialty', { context: 'plural' })}
          />
        );
      }

      return specialties.length > 0 ? (
        specialties.map((specialty) => (
          <div
            className="badge badge-newyellow-2 text-truncate mx-1"
            style={{ maxWidth: 'inherit' }}
            key={specialty}
          >
            {t(`specialty:${specialty}`)}
          </div>
        ))
      ) : <div />;
    };

    renderInvitations = () => {
      const {
        id, invitationCreationInProgress, processingInvitation, invitationEmail,
        invitationRole, canManageMembers, canEditAndDeleteTeam,
        invitationCanCreateProject, invitationCanInviteMembers, invitationCanManagePermissions,
        currentUserCanInviteMembers,
        // Organization team permissions
        invitationCanViewProjects, invitationCanCreateAndAdministrateProjects,
        invitationCanManagePublicProfile, invitationCanViewResearchTeams,
        invitationCanCreateAndAdministrateResearchTeams,
      } = this.state;
      const {
        t, teamMembers: tmMembers, userId, admin, team, user, fetchTeamMembers,
      } = this.props;
      const teamMembers = this.toTeamMembersArray(tmMembers);

      const leaders = teamMembers.filter((mb) => mb.leader && mb.team === id);
      const members = teamMembers.filter((mb) => !mb.leader && mb.team === id
            && mb.type === TEAM_MEMBER_USUAL);

      const isAdminTeam = team?.type === TEAM_TYPE_ORGANIZATION;
      const isInstitution = team?.owner?.type === USER_TYPE_HEALTHCARE_FACILITY;

      const membersListCommonProps = {
        className: 'mb-3',
        readOnly: !canManageMembers,
        userId,
        admin,
        canModifyItsOwnRole: canEditAndDeleteTeam,
        allTeamMembers: tmMembers,
        blockGuest: this.blockGuest,
        fetchTeamMembers,
      };

      const currentUserIsOwner = team && team.owner && team.owner.id === userId;
      // eslint-disable-next-line camelcase
      const isOwnerReadOnly = team?.is_owner_read_only_for_research_teams || false;
      // eslint-disable-next-line camelcase
      const canAdministrateViaAdminTeam = team?.current_user_can_administrate || false;
      // Owner can only invite if not read-only, admin team members with permission can invite too
      const showNewInvitationBtn = (admin || currentUserCanInviteMembers
        || canAdministrateViaAdminTeam) && (!currentUserIsOwner || !isOwnerReadOnly);
      const invitationFormValid = this.isInvitationFormValid();

      const teamFrozen = team ? team.is_frozen : false;

      const permissionsDisabled = teamFrozen && team.frozen_reason !== NO_TEAM_LEADER;
      const canInviteAsLeader = admin || canAdministrateViaAdminTeam;

      return (
        <div>
          {!isAdminTeam && (
            <>
              <MemberList
                role={ROLE_LEADER}
                teamMembers={leaders}
                teamFrozen={teamFrozen}
                team={team}
                {...membersListCommonProps}
                user={user}
              >
                {t('user:team.invitations.leaders')}
              </MemberList>

              <div className="text-muted font-italic small pb-3">
                {t('user:team.invitations.leader_invitation_info')}
              </div>
            </>
          )}

          <MemberList
            role={ROLE_MEMBER}
            teamMembers={members}
            teamFrozen={teamFrozen}
            team={team}
            {...membersListCommonProps}
            user={user}
          >
            {t('user:team.invitations.members')}
          </MemberList>
          {showNewInvitationBtn && (
            <div className="pt-3">
              <NewTooltip
                content={t('user:team.new-invitation.title-tooltip')}
                containerClassName="team-new-invitation-tooltip"
                placement="right"
                arrow
              >
                <button
                  className="btn btn-invite-in-a-team"
                  disabled={invitationCreationInProgress}
                  onClick={() => this.setState({ invitationCreationInProgress: true })}
                >
                  {t('user:team.new-invitation.title')}
                </button>
              </NewTooltip>
            </div>
          )}

          {invitationCreationInProgress && (
            <div className="col-12">
              <LabeledInput
                type="textarea"
                label={(
                  <span>
                    {t('user:team.new-invitation.email')}
                    <Help iconClassName="ml-2">
                      {t('user:team.new-invitation-info')}
                    </Help>
                  </span>
                                )}
                name="email"
                placeholder={t('error:placeholder.email')}
                validation="email_list"
                required
                className="mt-4 mb-0"
                colInputClassName="col-12"
                validatorTooltipDistance={35}
                value={invitationEmail}
                onChange={this.onEmailInvitationChange}
                onPaste={this.onEmailInvitationPaste}
                ref={(validator) => { this.emailValidator = validator; }}
              />
              <span className="help-text">
                {t('user:team.new-invitation.enter-emails-info')}
              </span>
              {!isAdminTeam && (
                <MemberRoleSelect
                  className="mb-4 mt-4"
                  label={t('user:team.new-invitation.role')}
                  value={invitationRole}
                  readOnly={!canManageMembers || teamFrozen}
                  onChange={(e) => {
                    if (e.target.value === ROLE_LEADER && !canInviteAsLeader) {
                      return;
                    }
                    this.setState({ invitationRole: e.target.value });
                  }}
                  canInviteAsLeader={canInviteAsLeader}
                />
              )}

              <div className={`mb-3 ${isAdminTeam ? 'mt-4' : ''}`}>
                <Label className="mb-2 project-invitation-step">
                  <InvitationStepLabel label={t('user:team.new-invitation.permissions')} />
                </Label>

                <div className="row text-left d-block">
                  <div className="col-12 text-left">

                    {isAdminTeam ? (
                      <>
                        {/* Section 1: Gestion du profil et de l'équipe d'organisation */}
                        <div className="font-weight-semibold text-underline mb-2">
                          {t('user:admin-team.permissions.section.profile-and-team')}
                        </div>

                        <div className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-manage-public-profile"
                            checked={invitationCanManagePublicProfile}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_MANAGE_PUBLIC_PROFILE)}
                            onChange={(e) => this.setState(
                              { invitationCanManagePublicProfile: e.target.checked },
                            )}
                          />
                          <label
                            className={`custom-control-label ${!this.userHasPermission(CAN_MANAGE_PUBLIC_PROFILE) ? 'text-muted' : ''}`}
                            htmlFor="invitation-can-manage-public-profile"
                          >
                            {t('user:admin-team.permissions.can_manage_public_profile')}
                            <Help iconClassName="ml-2">
                              {t('user:admin-team.permissions.can_manage_public_profile_help')}
                            </Help>
                          </label>
                        </div>

                        <div className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-invite-members"
                            checked={invitationCanInviteMembers}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_INVITE_TEAM_MEMBER)}
                            onChange={(e) => this.setState(
                              { invitationCanInviteMembers: e.target.checked },
                            )}
                          />
                          <label
                            className={`custom-control-label ${!this.userHasPermission(CAN_INVITE_TEAM_MEMBER) ? 'text-muted' : ''}`}
                            htmlFor="invitation-can-invite-members"
                          >
                            {t('user:admin-team.permissions.can_invite_team_member')}
                            <Help iconClassName="ml-2">
                              {t('user:admin-team.permissions.can_invite_team_member_help')}
                            </Help>
                          </label>
                        </div>

                        <div className="custom-control custom-checkbox mb-3">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-manage-permissions"
                            checked={invitationCanManagePermissions}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_MANAGE_TEAM_PERMISSIONS)}
                            onChange={(e) => this.setState(
                              { invitationCanManagePermissions: e.target.checked },
                            )}
                          />
                          <label
                            className={`custom-control-label ${!this.userHasPermission(CAN_MANAGE_TEAM_PERMISSIONS) ? 'text-muted' : ''}`}
                            htmlFor="invitation-can-manage-permissions"
                          >
                            {t('user:admin-team.permissions.can_manage_team_permissions')}
                            <Help iconClassName="ml-2">
                              {t('user:admin-team.permissions.can_manage_team_permissions_help')}
                            </Help>
                          </label>
                        </div>

                        {/* Section 2: Gestion des projets */}
                        <div className="font-weight-semibold text-underline mb-2">
                          {t('user:admin-team.permissions.section.projects')}
                        </div>

                        <div className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-view-projects"
                            checked={invitationCanViewProjects}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_VIEW_PROJECTS)}
                            onChange={(e) => this.setState(
                              { invitationCanViewProjects: e.target.checked },
                            )}
                          />
                          <label
                            className={`custom-control-label ${!this.userHasPermission(CAN_VIEW_PROJECTS) ? 'text-muted' : ''}`}
                            htmlFor="invitation-can-view-projects"
                          >
                            {t('user:admin-team.permissions.can_view_projects')}
                            <Help iconClassName="ml-2">
                              {t('user:admin-team.permissions.can_view_projects_help')}
                            </Help>
                          </label>
                        </div>

                        <div className={`custom-control custom-checkbox ${isInstitution ? 'mb-3' : 'mb-2'}`}>
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-create-and-administrate-projects"
                            checked={invitationCanCreateAndAdministrateProjects}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_CREATE_AND_ADMINISTRATE_PROJECTS)}
                            onChange={(e) => this.setState(
                              { invitationCanCreateAndAdministrateProjects: e.target.checked },
                            )}
                          />
                          <label
                            className={`custom-control-label ${!this.userHasPermission(CAN_CREATE_AND_ADMINISTRATE_PROJECTS) ? 'text-muted' : ''}`}
                            htmlFor="invitation-can-create-and-administrate-projects"
                          >
                            {t('user:admin-team.permissions.can_create_and_administrate_projects')}
                            <Help iconClassName="ml-2">
                              {t('user:admin-team.permissions.can_create_and_administrate_projects_help')}
                            </Help>
                          </label>
                        </div>

                        {/* Section 3: Gestion des équipes de recherche (institutions only) */}
                        {isInstitution && (
                          <>
                            <div className="font-weight-semibold text-underline mb-2">
                              {t('user:admin-team.permissions.section.research-teams')}
                            </div>

                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="invitation-can-view-research-teams"
                                checked={invitationCanViewResearchTeams}
                                disabled={permissionsDisabled
                                  || !this.userHasPermission(CAN_VIEW_RESEARCH_TEAMS)}
                                onChange={(e) => this.setState(
                                  { invitationCanViewResearchTeams: e.target.checked },
                                )}
                              />
                              <label
                                className={`custom-control-label ${!this.userHasPermission(CAN_VIEW_RESEARCH_TEAMS) ? 'text-muted' : ''}`}
                                htmlFor="invitation-can-view-research-teams"
                              >
                                {t('user:admin-team.permissions.can_view_research_teams')}
                                <Help iconClassName="ml-2">
                                  {t('user:admin-team.permissions.can_view_research_teams_help')}
                                </Help>
                              </label>
                            </div>

                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="invitation-can-create-and-administrate-research-teams"
                                checked={invitationCanCreateAndAdministrateResearchTeams}
                                disabled={permissionsDisabled
                                  // eslint-disable-next-line max-len
                                  || !this.userHasPermission(CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS)}
                                onChange={(e) => this.setState({
                                  invitationCanCreateAndAdministrateResearchTeams: e.target.checked,
                                })}
                              />
                              <label
                                // eslint-disable-next-line max-len
                                className={`custom-control-label ${!this.userHasPermission(CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS) ? 'text-muted' : ''}`}
                                htmlFor="invitation-can-create-and-administrate-research-teams"
                              >
                                {t('user:admin-team.permissions.can_create_and_administrate_research_teams')}
                                <Help iconClassName="ml-2">
                                  {t('user:admin-team.permissions.can_create_and_administrate_research_teams_help')}
                                </Help>
                              </label>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Standard team permissions */}
                        <div className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-create-project"
                            checked={invitationCanCreateProject}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_CREATE_PROJECT)}
                            onChange={(e) => this.setState(
                              { invitationCanCreateProject: e.target.checked },
                            )}
                          />
                          <label
                            className={`custom-control-label ${!this.userHasPermission(CAN_CREATE_PROJECT) ? 'text-muted' : ''}`}
                            htmlFor="invitation-can-create-project"
                          >
                            {t('user:team.permissions.can_create_project')}
                          </label>
                        </div>

                        <div className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-invite-members"
                            checked={invitationCanInviteMembers}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_INVITE_TEAM_MEMBER)}
                            onChange={(e) => this.setState(
                              { invitationCanInviteMembers: e.target.checked },
                            )}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="invitation-can-invite-members"
                          >
                            <span>{t('user:team.permissions.can_invite_team_member')}</span>
                            <Help iconClassName="ml-2">
                              {t('user:team.creation.permissions.help')}
                            </Help>
                          </label>
                        </div>

                        <div className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="invitation-can-manage-permissions"
                            checked={invitationCanManagePermissions}
                            disabled={permissionsDisabled
                              || !this.userHasPermission(CAN_MANAGE_TEAM_PERMISSIONS)}
                            onChange={(e) => this.setState(
                              { invitationCanManagePermissions: e.target.checked },
                            )}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="invitation-can-manage-permissions"
                          >
                            {t('user:team.permissions.can_manage_team_permissions')}
                          </label>
                        </div>
                      </>
                    )}

                  </div>
                </div>
                <div className="text-muted font-italic small mt-2">
                  {t('user:team.permissions.cannot_assign_without_having')}
                </div>
              </div>

              <button
                className={`btn btn-invite-in-a-team text-white mt-1 ${invitationFormValid ? '' : 'cursor-default'}`}
                disabled={!invitationFormValid}
                onClick={this.invite}
              >
                <span>
                  {t('common:button.invite')}
                </span>
                <div className="d-inline-block ml-1">
                  { processingInvitation && <CardLoader /> }
                </div>
              </button>
              <button
                disabled={processingInvitation}
                className="ml-4 btn btn-link"
                type="button"
                onClick={() => this.setState({
                  invitationCreationInProgress: false,
                  invitationCanCreateProject: false,
                  invitationCanInviteMembers: false,
                  invitationCanManagePermissions: false,
                  // Reset organization team permissions
                  invitationCanViewProjects: false,
                  invitationCanCreateAndAdministrateProjects: false,
                  invitationCanManagePublicProfile: false,
                  invitationCanViewResearchTeams: false,
                  invitationCanCreateAndAdministrateResearchTeams: false,
                })}
              >
                {t('common:button.cancel')}
              </button>
            </div>
          )}
        </div>
      );
    };

    renderDeleteTeam = () => {
      const { t } = this.props;
      return (
        <DelMsgModal
          message={(
            <span>
              <b>
                {t('common:caution')}
                  &nbsp;!
              </b>
              <br />
              <br />
              {t('user:team.deletion-warning')}
              <br />
              <br />
              {t('common:confirm-deletion')}
            </span>
                )}
          onValidate={this.deleteTeam}
        >
          <ButtonConfirm>
            <button
              type="button"
              className="btn btn-primary"
            >
              {t('user:team.delete')}
            </button>
          </ButtonConfirm>
        </DelMsgModal>
      );
    };

    renderQuitTeam = () => {
      const { t, admin } = this.props;
      const { id } = this.state;

      return (
        <DelMsgModal
          message={(
            <span>
              <b>
                {t('common:caution')}
                  &nbsp;!
              </b>
              <br />
              <br />
              {t('user:team.quit-warning')}
              <br />
              <br />
              {t('common:confirm-deletion')}
            </span>
                )}
          onValidate={this.quitTeam}
        >
          <ButtonConfirm>
            <button
              type="button"
              className="btn btn-primary"
              disabled={admin && !id}
            >
              {t('user:team.quit')}
            </button>
          </ButtonConfirm>
        </DelMsgModal>
      );
    };

    render() {
      const {
        t, admin, team, userId,
      } = this.props;
      const {
        loading, id, nameEditing, type, owner, canEditAndDeleteTeam,
      } = this.state;

      const isAdminTeam = team?.type === TEAM_TYPE_ORGANIZATION;
      const showDeleteTeam = canEditAndDeleteTeam;
      const isOwner = owner === userId;

      const ownerTitle = (
        <>
          <span className={isAdminTeam ? '' : 'mr-2'}>{t('user:team.title.owner')}</span>
          {!isAdminTeam && (
            <Help interactive>
              <div>
                <div className="mb-1">
                  {t('user:team.help.owner.title')}
                </div>
                <ul className="mb-0 owner-rights-list">
                  {t('user:team.help.owner.list', { returnObjects: true })
                    .map((item) => (
                      <li key={item}>
                        {item}
                      </li>
                    ))}
                </ul>
              </div>
            </Help>
          )}
        </>
      );

      return (
        <div>
          { loading && <CardLoader /> }
          {(!admin || id) && (
            <div className="new-settings mx-4">
              <RowContent
                contentClassName="col-12 col-md-8 col-lg-7 col-xl-7 mt-1"
                label={(
                  <>
                    <span>
                      {t('user:team.title.name')}
                    </span>
                    {canEditAndDeleteTeam && (
                    <>
                      <span className="ml-2" />
                      <EditButton
                        active={nameEditing}
                        transform="grow-2"
                        onClick={this.onNameEditionToggle}
                      />
                    </>
                    )}
                  </>
                            )}
                show={Boolean(team && (admin || !isAdminTeam))}
              >
                {this.renderName()}
              </RowContent>
              {owner && (
              <RowContent
                label={ownerTitle}
                contentClassName="col-auto"
                show={Boolean(team)}
              >
                {this.renderOwner()}
              </RowContent>
              )}
              {team && team.administrators && team.administrators.length > 0 && (
              <RowContent
                label={t('user:team.title.administrators')}
                contentClassName="col-auto"
                show={Boolean(team)}
              >
                {this.renderAdministrators()}
              </RowContent>
              )}
              {type && (
              <RowContent
                contentClassName="col-12 col-md-8 mt-2"
                label={t('user:team.title.type')}
                show={Boolean(team && (admin || !isAdminTeam))}
              >
                {this.renderType()}
              </RowContent>
              )}
              <RowContent
                contentClassName="col-12 col-sm-8 col-md-5 col-lg-5 col-xl-4 mt-2"
                label={(
                  <>
                    {t('user:team.title.specialties')}
                    <small className="text-gray font-italic">
                      {t('common:form.optional-field')}
                    </small>
                  </>
                            )}
                show={Boolean(team && (admin || (team.specialties && team.specialties.length > 0)))}
              >
                {this.renderSpecialties()}
              </RowContent>
              <RowContent
                contentClassName="col-12 col-md-8 mt-2"
                label={t('user:team.title.invitations')}
                show={Boolean(team)}
              >
                {this.renderInvitations()}
              </RowContent>
              <RowContent
                label={t('user:team.title.delete')}
                show={Boolean(showDeleteTeam && team && (admin || !isAdminTeam))}
              >
                {this.renderDeleteTeam()}
              </RowContent>
              <RowContent
                label={t('user:team.title.leaving')}
                show={Boolean(
                  !showDeleteTeam && team && this.isCurrentUserMember() && !isOwner && !isAdminTeam,
                )}
              >
                {this.renderQuitTeam()}
              </RowContent>
            </div>
          )}
        </div>
      );
    }
}


export default TeamForm;
